import { useState, useEffect, useCallback, useMemo } from 'react'
import ApiLeagues from '../../../../api/ApiLeagues'
import ApiDivisions from '../../../../api/ApiDivisions'
import services from '../../../../services'
import toastr from 'toastr'
import { toUserFriendlyErrorMessage } from '../../../../utils/userFacingError'
import {
  processOpponentSlots,
  checkUserOverlap,
  findMatchProposalForOpponent,
  findLatestProposalForOpponent,
  findScheduledMatchForOpponent,
  findExistingMatchWithOpponent,
  buildOpponentTeamFromSlots,
} from '../utils/playerHistorySelectors'

const useAvailabilityData = ({
  fromLeague,
  leagueId,
  divisionId,
  playerHistory,
}) => {
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [availabilityError, setAvailabilityError] = useState(null)
  const [opponentAvailability, setOpponentAvailability] = useState([])
  const [userAvailability, setUserAvailability] = useState([])
  const [matchProposals, setMatchProposals] = useState([])
  const [allMatches, setAllMatches] = useState([])
  const [currentUserPlayerId, setCurrentUserPlayerId] = useState(null)
  const [leagueName, setLeagueName] = useState('')
  const [divisionName, setDivisionName] = useState('')
  const [actualDivisionId, setActualDivisionId] = useState(null)

  useEffect(() => {
    const fetchAvailabilityData = async () => {
      if (!fromLeague || !leagueId || !playerHistory?.player?.user_id) {
        return
      }

      try {
        setAvailabilityLoading(true)
        setAvailabilityError(null)

        const availabilityResponse =
          await services.availability.getLeagueAvailability(
            leagueId,
            divisionId,
          )

        if (!availabilityResponse.success) {
          throw new Error('Failed to load availability data')
        }

        const parsedUserAvailability = (
          availabilityResponse.data.userAvailability || []
        ).map((slot) => ({
          ...slot,
          start_time: slot.start_time ? new Date(slot.start_time) : null,
          end_time: slot.end_time ? new Date(slot.end_time) : null,
        }))
        setUserAvailability(parsedUserAvailability)

        const allOpponentAvailability = (
          availabilityResponse.data.opponentAvailability || []
        ).map((slot) => ({
          ...slot,
          start_time: slot.start_time ? new Date(slot.start_time) : null,
          end_time: slot.end_time ? new Date(slot.end_time) : null,
        }))

        const targetUserId = playerHistory.player.user_id
        const filteredAvailability = allOpponentAvailability.filter((slot) => {
          if (!targetUserId) {
            return false
          }
          const parsedTarget = parseInt(targetUserId, 10)
          // For doubles slots, opponent_team_user_ids includes all team members.
          // Check any team member so visiting any partner's page shows the same availability.
          if (Array.isArray(slot.opponent_team_user_ids)) {
            return slot.opponent_team_user_ids.some(
              (id) => parseInt(id, 10) === parsedTarget,
            )
          }
          // Singles fallback
          return (
            slot.opponent_user_id &&
            parseInt(slot.opponent_user_id, 10) === parsedTarget
          )
        })

        setOpponentAvailability(filteredAvailability)

        const proposalsResponse =
          await services.matchProposal.getReceivedProposals(
            leagueId,
            divisionId,
          )

        if (proposalsResponse.success) {
          const mapped = (proposalsResponse.data || []).map((proposal) => ({
            ...proposal,
            proposed_time: proposal.proposed_time
              ? new Date(proposal.proposed_time)
              : null,
          }))
          setMatchProposals(mapped)
        }

        const leagueResponse = await ApiLeagues.getLeague(leagueId)
        if (leagueResponse && leagueResponse.name) {
          setLeagueName(leagueResponse.name)
        }

        if (divisionId) {
          const divisionsResponse = await ApiDivisions.getAllByLeague(leagueId)
          const divisionsData = divisionsResponse?.success
            ? divisionsResponse.data
            : divisionsResponse

          if (divisionsData && Array.isArray(divisionsData)) {
            const division = divisionsData.find(
              (d) => parseInt(d.id, 10) === parseInt(divisionId, 10),
            )
            if (division && division.name) {
              setDivisionName(division.name)
            }
          }
        }

        const myDivisionResponse = await services.league.getMyDivision(leagueId)
        if (myDivisionResponse.success) {
          if (myDivisionResponse.data?.division_id) {
            setActualDivisionId(myDivisionResponse.data.division_id)
          }

          if (myDivisionResponse.data?.player_id) {
            setCurrentUserPlayerId(myDivisionResponse.data.player_id)
          }

          const matches =
            myDivisionResponse.data.matches?.map((match) => ({
              ...match,
              match_datetime: match.match_datetime
                ? new Date(match.match_datetime)
                : null,
            })) || []
          setAllMatches(matches)
        }
      } catch (err) {
        console.error('Error fetching opponent availability:', err)
        setAvailabilityError(
          'Failed to load availability. You can still view match history.',
        )
      } finally {
        setAvailabilityLoading(false)
      }
    }

    fetchAvailabilityData()
  }, [fromLeague, leagueId, divisionId, playerHistory])

  const refreshProposals = useCallback(async () => {
    if (!leagueId || !actualDivisionId) return

    try {
      const proposalsResponse =
        await services.matchProposal.getReceivedProposals(
          leagueId,
          actualDivisionId,
        )

      if (proposalsResponse.success) {
        const mapped = (proposalsResponse.data || []).map((proposal) => ({
          ...proposal,
          proposed_time: proposal.proposed_time
            ? new Date(proposal.proposed_time)
            : null,
        }))
        setMatchProposals(mapped)
      }
    } catch (error) {
      console.error('Error refreshing proposals:', error)
    }
  }, [leagueId, actualDivisionId])

  return {
    availabilityLoading,
    availabilityError,
    opponentAvailability,
    userAvailability,
    matchProposals,
    allMatches,
    currentUserPlayerId,
    leagueName,
    divisionName,
    actualDivisionId,
    refreshProposals,
  }
}

const useMatchProposalModal = ({ actualDivisionId, refreshProposals }) => {
  const [proposalModalOpen, setProposalModalOpen] = useState(false)
  const [selectedOpponent, setSelectedOpponent] = useState(null)
  const [proposedTime, setProposedTime] = useState(null)
  const [availabilityStart, setAvailabilityStart] = useState(null)
  const [availabilityEnd, setAvailabilityEnd] = useState(null)
  const [directProposal, setDirectProposal] = useState(false)

  const handleProposeMatch = useCallback(
    (opponent, date, hour, minute = 0, availStart = null, availEnd = null) => {
      const proposedDateTime = new Date(date)
      proposedDateTime.setHours(hour, minute, 0, 0)

      setSelectedOpponent(opponent)
      setProposedTime(proposedDateTime.toISOString())
      setAvailabilityStart(availStart)
      setAvailabilityEnd(availEnd)
      setDirectProposal(false)
      setProposalModalOpen(true)
    },
    [],
  )

  const handleDirectProposeMatch = useCallback((opponent) => {
    setSelectedOpponent(opponent)
    setProposedTime(null)
    setAvailabilityStart(null)
    setAvailabilityEnd(null)
    setDirectProposal(true)
    setProposalModalOpen(true)
  }, [])

  const handleMatchProposalConfirm = useCallback(
    async (
      opponentPlayerId,
      proposedTime,
      leagueId,
      expiresAt,
      notes,
      isDirectProposal,
    ) => {
      if (!actualDivisionId) {
        toastr.error(
          'Unable to create match proposal: division information not available',
        )
        setProposalModalOpen(false)
        return
      }

      try {
        const result = await services.matchProposal.createProposal(
          opponentPlayerId,
          proposedTime,
          leagueId,
          actualDivisionId,
          expiresAt,
          {
            notes,
            directProposal: isDirectProposal,
          },
        )

        if (result.success) {
          toastr.success('Match proposal sent successfully!')
          setProposalModalOpen(false)
          await new Promise((resolve) => setTimeout(resolve, 100))
          await refreshProposals()
        } else {
          toastr.error(
            toUserFriendlyErrorMessage(
              result.error,
              'Failed to send match proposal',
            ),
          )
        }
      } catch (error) {
        console.error('Error creating match proposal:', error)
        toastr.error(
          toUserFriendlyErrorMessage(error, 'Failed to send match proposal'),
        )
      }
    },
    [actualDivisionId, refreshProposals],
  )

  return {
    proposalModalOpen,
    setProposalModalOpen,
    selectedOpponent,
    proposedTime,
    availabilityStart,
    availabilityEnd,
    directProposal,
    handleProposeMatch,
    handleDirectProposeMatch,
    handleMatchProposalConfirm,
  }
}

export default function usePlayerHistoryAvailability({
  fromLeague,
  leagueId,
  divisionId,
  playerHistory,
}) {
  const {
    availabilityLoading,
    availabilityError,
    opponentAvailability,
    userAvailability,
    matchProposals,
    allMatches,
    currentUserPlayerId,
    leagueName,
    divisionName,
    actualDivisionId,
    refreshProposals,
  } = useAvailabilityData({ fromLeague, leagueId, divisionId, playerHistory })

  const {
    proposalModalOpen,
    setProposalModalOpen,
    selectedOpponent,
    proposedTime,
    availabilityStart,
    availabilityEnd,
    directProposal,
    handleProposeMatch,
    handleDirectProposeMatch,
    handleMatchProposalConfirm,
  } = useMatchProposalModal({ actualDivisionId, refreshProposals })

  const hasUserOverlap = useCallback(
    (combinedSlot) => checkUserOverlap(combinedSlot, userAvailability),
    [userAvailability],
  )

  const getMatchProposalForOpponent = useCallback(
    (opponentId, date, hour, slotEndTime = null) =>
      findMatchProposalForOpponent(
        matchProposals,
        opponentId,
        date,
        hour,
        slotEndTime,
      ),
    [matchProposals],
  )

  const getLatestProposalForOpponent = useCallback(
    (opponentId) => findLatestProposalForOpponent(matchProposals, opponentId),
    [matchProposals],
  )

  const getScheduledMatchForOpponent = useCallback(
    (opponentId, date, hour) =>
      findScheduledMatchForOpponent(allMatches, opponentId, date, hour),
    [allMatches],
  )

  const getExistingMatchWithOpponent = useCallback(
    (opponentId) => findExistingMatchWithOpponent(allMatches, opponentId),
    [allMatches],
  )

  const processedOpponents = useMemo(() => {
    if (!playerHistory?.player || !fromLeague) {
      return []
    }

    const player = playerHistory.player

    // For doubles, the server enriches each slot with the opposing team's
    // identity (opponent_team_id), member user_ids, and player records so the
    // client can both label and (in future) look up by team. We surface that
    // as an explicit `team` sub-object on the processed opponent so consumers
    // can distinguish the page-target *player* identity (id/user_id/first_name/
    // last_name) from the opposing *team* identity. For singles, `team` is
    // null and consumers fall back to the player fields. See issue #2325.
    //
    // Note: useAvailabilityData filters opponentAvailability to slots whose
    // opponent_team_user_ids contain the page-target user, so every remaining
    // doubles slot references the same opposing team. Reading team metadata
    // from the first matching slot is therefore safe.
    const team = buildOpponentTeamFromSlots(opponentAvailability, player.id)

    return [
      {
        // Page-target player identity (used for routing and singles lookups).
        id: player.id,
        user_id: player.user_id,
        first_name: player.first_name,
        last_name: player.last_name,
        // Opposing-team identity for doubles; null for singles.
        team,
        slots: processOpponentSlots(opponentAvailability),
      },
    ]
  }, [playerHistory, fromLeague, opponentAvailability])

  return {
    availabilityLoading,
    availabilityError,
    processedOpponents,
    handleProposeMatch,
    handleDirectProposeMatch,
    hasUserOverlap,
    getMatchProposalForOpponent,
    getLatestProposalForOpponent,
    getScheduledMatchForOpponent,
    getExistingMatchWithOpponent,
    currentUserPlayerId,
    allMatches,
    leagueName,
    divisionName,
    proposalModalOpen,
    setProposalModalOpen,
    selectedOpponent,
    proposedTime,
    availabilityStart,
    availabilityEnd,
    directProposal,
    handleMatchProposalConfirm,
  }
}
